import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Button, { ButtonSizes, ButtonThemes } from "../../../../ui-components/button/button";
import ConfirmationModal from "../../../../ui-components/confirmation-modal/confirmationModal";
import InvoiceFactoringService from "../../../../app/data/invoice-factoring/invoiceFactoringService";
import { DenimUploadBatch } from "../../../../app/data/invoice-factoring/models";
import { BatchAction, getAvailableBatchActions, getRemainingCount } from "../utils/nextAction";
import { pluralize } from "../constants";

const invoiceFactoringService = InvoiceFactoringService.getInstance();

interface ActionConfig {
  button: string;
  theme: ButtonThemes;
  header: string;
  confirm: string;
  success: string;
  failure: string;
  run: (batchId: string) => Promise<DenimUploadBatch>;
  body: (batch: DenimUploadBatch) => string;
}

const ACTIONS: Record<BatchAction, ActionConfig> = {
  start: {
    button: "Start sending",
    theme: ButtonThemes.blue,
    header: "Start Sending",
    confirm: "Start sending",
    success: "Batch queued for sending.",
    failure: "Unable to start the batch.",
    run: invoiceFactoringService.startBatch,
    body: (batch) =>
      `${pluralize(getRemainingCount(batch), "probill")} will be sent to Denim. Probills already sent are recorded as already sent and are not sent again.`,
  },
  stop: {
    button: "Stop",
    theme: ButtonThemes.red,
    header: "Stop Batch",
    confirm: "Stop batch",
    success: "Stop requested.",
    failure: "Unable to stop the batch.",
    run: invoiceFactoringService.stopBatch,
    body: (batch) =>
      batch.status === "RUNNING"
        ? "The invoice being sent now finishes first, then the batch stops. You can resume it later to send the remaining probills."
        : "The batch stops before sending anything else. You can resume it later.",
  },
  resume: {
    button: "Resume",
    theme: ButtonThemes.blue,
    header: "Resume Batch",
    confirm: "Resume",
    success: "Batch queued to resume.",
    failure: "Unable to resume the batch.",
    run: invoiceFactoringService.resumeBatch,
    body: (batch) =>
      `${pluralize(getRemainingCount(batch), "remaining probill")} will be sent. Steps already completed for an invoice are skipped.`,
  },
  retryFailed: {
    button: "Retry failed",
    theme: ButtonThemes.blueInverted,
    header: "Retry Failed Invoices",
    confirm: "Retry failed",
    success: "Failed invoices queued for another attempt.",
    failure: "Unable to retry the failed invoices.",
    run: invoiceFactoringService.retryFailedItems,
    body: (batch) =>
      `${pluralize(batch.failedCount, "failed probill")} will be sent again. Steps that already succeeded, such as job creation, are skipped.`,
  },
};

export interface BatchActionsProps {
  batch: DenimUploadBatch;
  onChanged: () => void;
}

const BatchActions: React.FC<BatchActionsProps> = ({ batch, onChanged }) => {
  const [pending, setPending] = useState<BatchAction | null>(null);

  const mutation = useMutation({
    mutationFn: (action: BatchAction) => ACTIONS[action].run(batch.id),
    onSuccess: (_data, action) => {
      toast.info(ACTIONS[action].success);
      setPending(null);
      onChanged();
    },
    onError: (e: Error, action) => {
      toast.error(e.message || ACTIONS[action].failure);
      setPending(null);
      onChanged();
    },
  });

  const actions = getAvailableBatchActions(batch);
  if (!actions.length) return null;

  return (
    <>
      <div className="xgs-invoice-factoring__actions">
        {actions.map((action) => (
          <Button
            key={action}
            theme={ACTIONS[action].theme}
            size={ButtonSizes.auto}
            type="button"
            disabled={mutation.isLoading}
            onClick={() => setPending(action)}
          >
            {ACTIONS[action].button}
          </Button>
        ))}
      </div>
      <ConfirmationModal
        opened={!!pending}
        header={pending ? ACTIONS[pending].header : ""}
        confirmButtonText={pending ? ACTIONS[pending].confirm : ""}
        spinner={mutation.isLoading}
        fitContent
        onCancel={() => setPending(null)}
        onConfirm={() => pending && mutation.mutate(pending)}
      >
        {pending ? ACTIONS[pending].body(batch) : null}
      </ConfirmationModal>
    </>
  );
};

export default BatchActions;
